import React from 'react';
import { Pressable } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';
import { ToggleIconButton } from 'app/shopify/components/actions/ToggleIconButton/ToggleIconButton';

type ToggleButtonState = 'enabled' | 'hover' | 'pressed';
type ToggleButtonType = 'default' | 'outline' | 'ghost';
type ToggleButtonGroupType = 'default' | 'iconOnly';

export interface ToggleButtonProps {
  label: string;
  selected?: boolean;
  /**
   * Figma: non-selected décline en `ghost` ou `outline`.
   * Quand `selected=true`, le style "on" (default rempli) est prioritaire.
   */
  type?: ToggleButtonType;
  state?: ToggleButtonState;
  onPress?: () => void;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

export interface ToggleButtonGroupOption {
  key: string;
  label?: string;
  icon?: React.ReactNode;
  selectedIcon?: React.ReactNode;
}

export interface ToggleButtonGroupProps {
  selectedIndex: number;
  onChange: (index: number) => void;
  options: ToggleButtonGroupOption[];
  type?: ToggleButtonGroupType;
  state?: ToggleButtonState;
  buttonType?: Extract<ToggleButtonType, 'ghost' | 'outline'>;
}

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

export function ToggleButton({
  label,
  selected = false,
  type = 'ghost',
  state,
  onPress,
  leadingIcon,
  trailingIcon,
}: ToggleButtonProps) {
  const theme = useTheme();
  const isInteractive = state === undefined || state === 'enabled';
  const isOutline = !selected && type === 'outline';
  const isSelected = selected;

  const backgroundColor = isSelected ? theme.colors['surface.brand.strong'] : 'transparent';
  const textColor = isSelected ? theme.colors['text.inverse.primary'] : theme.colors['text.brand'];
  const borderColor = isOutline ? theme.colors['border.secondary'] : 'transparent';
  const borderWidth = isOutline ? 1 : 0;

  function getOverlayColor(showPressed: boolean, showHover: boolean) {
    if (!showPressed && !showHover) return undefined;
    return theme.colors[showPressed ? 'state-layer.press-brand' : 'state-layer.hover-brand'];
  }

  function renderInner(interaction?: PressableInteractionState) {
    const pressed = interaction?.pressed ?? false;
    const hovered = interaction?.hovered ?? false;
    const showPressed = isInteractive ? pressed : state === 'pressed';
    const showHover = isInteractive ? hovered && !pressed : state === 'hover';
    const overlayColor = getOverlayColor(showPressed, showHover);
    const overlayInset = isOutline && (showHover || showPressed) ? -1 : 0;

    return (
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        borderRadius="radius.full"
        minHeight={40}
        height={40}
        paddingHorizontal="spacing.general.x3"
        style={{ backgroundColor, borderColor, borderWidth }}
      >
        {overlayColor ? (
          <Box
            position="absolute"
            top={overlayInset}
            left={overlayInset}
            right={overlayInset}
            bottom={overlayInset}
            borderRadius="radius.full"
            style={{ backgroundColor: overlayColor, zIndex: 0 }}
          />
        ) : null}

        <Box flexDirection="row" alignItems="center" justifyContent="center" style={{ zIndex: 1 }}>
          {leadingIcon ? (
            <Box alignItems="center" justifyContent="center" width={24} height={24} marginRight="spacing.general.x1">
              {leadingIcon}
            </Box>
          ) : null}

          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            paddingHorizontal="spacing.general.x2"
            paddingTop="spacing.general.0"
            paddingBottom="spacing.general.x0,5"
          >
            <Text variant="bodyBaseMedium" style={{ color: textColor }}>
              {label}
            </Text>
          </Box>

          {trailingIcon ? (
            <Box alignItems="center" justifyContent="center" width={24} height={24} marginLeft="spacing.general.x1">
              {trailingIcon}
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (isInteractive) {
    return (
      <Pressable onPress={onPress}>
        {(pressableState) =>
          renderInner({
            pressed: pressableState.pressed,
            hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
          })
        }
      </Pressable>
    );
  }

  return <Pressable onPress={onPress}>{renderInner()}</Pressable>;
}

export function ToggleButtonGroup({
  selectedIndex,
  onChange,
  options,
  type = 'default',
  state,
  buttonType = 'ghost',
}: ToggleButtonGroupProps) {
  const isIconOnly = type === 'iconOnly';

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      gap="spacing.general.0"
      padding="spacing.general.x1"
      borderRadius="radius.full"
      backgroundColor="surface.primary-neutral"
    >
      {options.map((option, index) => {
        const selected = index === selectedIndex;

        if (isIconOnly) {
          return (
            <ToggleIconButton
              key={option.key}
              selected={selected}
              type={selected ? 'primary' : 'ghost'}
              size="medium"
              state={state}
              icon={option.icon}
              selectedIcon={option.selectedIcon ?? option.icon}
              onPress={() => onChange(index)}
            />
          );
        }

        return (
          <ToggleButton
            key={option.key}
            label={option.label ?? ''}
            selected={selected}
            type={selected ? 'default' : buttonType}
            state={state}
            onPress={() => onChange(index)}
          />
        );
      })}
    </Box>
  );
}
